import PropTypes from 'prop-types'
import React from 'react'
import { Redirect, Route } from 'react-router-dom'
import { useSelector } from 'react-redux'
import * as Sentry from '@sentry/react'

import permissionGroups from '../../utils/accessGroupsPermissions.json'
import useHasPermission from 'hooks/useHasPermission'

// Create Custom Sentry Route component
const SentryRoute = Sentry.withSentryRouting(Route)

/**
 * Wrapper for default React route because pages - dashboard, recommendations and others are available only for logged in users here we decide if the have access or should be redirected to login page.
 * @Tags( presentional )
 */
const PrivateRoute = ({ component: Component, permissionsName, ...rest }) => {
    const user = useSelector(store => store.user)
    const accessLevel = user ? user.accessLevel : 'webapp-access-group-1'
    const hasPermission = useHasPermission(permissionsName)

    const { needsFeatureAccess, pageName, needsUpdate, partnerId } = rest

    if (needsUpdate) {
        return <Redirect to={`/update-user/${partnerId}`} />
    }

    if (!user.isLogged || !user.accessLevel || !user.partnerId) return <Redirect to={{ pathname: '/login' }} />

    if (user.isLogged && !permissionGroups[accessLevel].pages[pageName]) {
        return (
            <SentryRoute render={props =>
                <Redirect to={{ pathname: '/', state: { from: props.location } }}
                />
            }
            />
        )
    }

    // add account emails for features in './utils/flage-features' to white list emails to features in PRD
    if (needsFeatureAccess) {
        if (hasPermission) {
            return (
                <SentryRoute
                    {...rest}
                    render={props => user.isLogged ? (
                        <Component {...props} />
                    ) : (
                        <Redirect to={{ pathname: '/login', state: { from: props.location } }} />
                    )
                    }
                />
            )
        } else {
            return (
                <SentryRoute
                    render={props => <Redirect to={{ pathname: '/', state: { from: props.location } }} />}
                />
            )
        }

    }

    // SUCCESS - user logged in and redirected to /
    return (
        <SentryRoute
            {...rest}
            render={props => user.isLogged ? (
                <Component {...props} {...rest} />
            ) : (
                <Redirect to={{ pathname: '/login', state: { from: props.location } }} />
            )
            }
        />
    )
}

PrivateRoute.propTypes = {
    /**
   * PageComponent 
   * */
    component: PropTypes.elementType.isRequired,
    permissionsName: PropTypes.string,
    location: PropTypes.object
}

export default PrivateRoute